import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { KeyRound, Loader2, Save, ShieldAlert, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermission } from '@/features/auth/auth.store';
import { GENDER, LANGUAGE } from '@/features/auth/register.api';
import { cn } from '@/lib/utils';
import {
  ASSIGNABLE_ROLES,
  USER_FLAGS,
  useDeleteUser,
  useGenerateManagerCode,
  useManagerCode,
  useResetLoginCode,
  useResetPasswordRandom,
  useSendLoginCode,
  useSetPassword,
  useToggleRole,
  useUpdateFlag,
  useUpdateProfile,
  type UserFlag,
} from './edit-customer.api';
import type { Customer } from './types';

/** Maps each API flag to its label and the field on the customer record. */
const FLAG_META: Record<UserFlag, { labelKey: string; read: (c: Customer) => boolean }> = {
  AllowEmails: { labelKey: 'customers.allowEmails', read: (c) => c.allowEmails },
  AllowPush: { labelKey: 'customers.allowPush', read: (c) => c.allowPush },
  AllowSMS: { labelKey: 'customers.allowSms', read: (c) => c.allowSMS },
  IsBlocked: { labelKey: 'customers.isBlocked', read: (c) => Boolean(c.isBlocked) },
  IsGuestUser: { labelKey: 'customers.isGuest', read: (c) => Boolean(c.isGuestUser) },
  CanViewBetaStores: { labelKey: 'customers.betaStores', read: (c) => Boolean(c.canViewBetaStores) },
  CanViewAlphaStores: { labelKey: 'customers.alphaStores', read: (c) => Boolean(c.canViewAlphaStores) },
  CanMakeOrders: { labelKey: 'customers.canMakeOrders', read: (c) => Boolean(c.canMakeOrders) },
  CanViewBetaInfoCards: { labelKey: 'customers.betaInfoCards', read: (c) => Boolean(c.canViewBetaInfoCards) },
  IsHighRisk: { labelKey: 'customers.isHighRisk', read: (c) => Boolean(c.isHighRisk) },
};

const profileSchema = z.object({
  firstName: z.string().trim().min(1, 'auth.validation.firstNameRequired'),
  lastName: z.string().trim(),
  email: z.string().trim().email('auth.validation.emailInvalid'),
  phoneNumber: z.string().trim(),
  gender: z.coerce.number(),
  preferredLanguage: z.coerce.number(),
  externalClientId: z.string().trim(),
});

type ProfileValues = z.infer<typeof profileSchema>;

/** A switch row that reports its own success and rolls back on failure. */
function FlagRow({
  customer,
  flag,
  disabled,
}: {
  customer: Customer;
  flag: UserFlag;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const meta = FLAG_META[flag];
  const [checked, setChecked] = useState(meta.read(customer));
  const update = useUpdateFlag(customer.id);

  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <Label
        htmlFor={`flag-${flag}`}
        className={cn(
          'text-sm font-normal',
          (flag === 'IsBlocked' || flag === 'IsHighRisk') &&
            checked &&
            'text-destructive font-medium'
        )}
      >
        {t(meta.labelKey)}
      </Label>
      <Switch
        id={`flag-${flag}`}
        checked={checked}
        disabled={disabled || update.isPending}
        onCheckedChange={(next) => {
          // Optimistic, because a switch that does not move on tap reads as
          // broken; the previous value is restored if the request fails.
          setChecked(next);
          update.mutate(
            { flag, value: next },
            {
              onError: () => {
                setChecked(!next);
                toast.error(t('customers.updateFailed'));
              },
              onSuccess: () => toast.success(t('customers.updated')),
            }
          );
        }}
      />
    </div>
  );
}

function RoleRow({
  customer,
  role,
  assigned,
  disabled,
}: {
  customer: Customer;
  role: string;
  assigned: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(assigned);
  const toggle = useToggleRole(customer.id);

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <Label htmlFor={`role-${role}`} className="text-sm font-normal">
        {role}
      </Label>
      <Switch
        id={`role-${role}`}
        checked={checked}
        disabled={disabled || toggle.isPending}
        onCheckedChange={(next) => {
          setChecked(next);
          toggle.mutate(role, {
            onError: () => {
              setChecked(!next);
              toast.error(t('customers.roleFailed'));
            },
            onSuccess: () => toast.success(t('customers.roleUpdated')),
          });
        }}
      />
    </div>
  );
}

/** Action buttons that return a secret; the result is copied and shown. */
function SecretAction({
  label,
  pending,
  onRun,
  variant = 'outline',
  icon,
}: {
  label: string;
  pending: boolean;
  onRun: () => void;
  variant?: 'outline' | 'destructive';
  icon?: React.ReactNode;
}) {
  return (
    <Button
      variant={variant}
      className="w-full justify-start"
      disabled={pending}
      onClick={onRun}
    >
      {pending ? <Loader2 className="animate-spin" /> : icon}
      {label}
    </Button>
  );
}

export function EditCustomerSheet({
  customer,
  open,
  onOpenChange,
}: {
  customer: Customer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const isAdmin = usePermission('isSuperAdmin');
  const isStoreManager = usePermission('isStoreManager');
  const [customPassword, setCustomPassword] = useState('');

  // Every hook below needs an id; the sheet only renders with a customer.
  const id = customer?.id ?? '';
  const updateProfile = useUpdateProfile(id);
  const resetRandom = useResetPasswordRandom(id);
  const setPassword = useSetPassword(id);
  const sendLoginCode = useSendLoginCode(id);
  const resetLoginCode = useResetLoginCode(id);
  const managerCode = useManagerCode(id);
  const generateManagerCode = useGenerateManagerCode(id);
  const deleteUser = useDeleteUser(customer?.friendCode ?? '');

  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    values: {
      firstName: customer?.firstName ?? '',
      lastName: customer?.lastName ?? '',
      email: customer?.email ?? '',
      phoneNumber: customer?.phoneNumber ?? '',
      gender: customer?.gender ?? GENDER.male,
      preferredLanguage: customer?.preferredLanguage ?? LANGUAGE.en,
      externalClientId: customer?.externalClientId ?? '',
    },
  });

  if (!customer) return null;

  /** Copies a returned secret and says so — several endpoints put it in `message`. */
  const announce = (message: string) => {
    if (!message) return;
    void navigator.clipboard?.writeText(message).catch(() => undefined);
    toast.success(message, { description: t('customers.copiedToClipboard') });
  };

  const fail = () => toast.error(t('customers.actionFailed'));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b">
          <SheetTitle>
            {[customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
              t('customers.editUser')}
          </SheetTitle>
          <SheetDescription>{customer.email}</SheetDescription>
        </SheetHeader>

        <div className="p-4">
          <Tabs defaultValue="profile">
            <TabsList>
              <TabsTrigger value="profile">
                {t('customers.tabProfile')}
              </TabsTrigger>
              <TabsTrigger value="flags">{t('customers.tabFlags')}</TabsTrigger>
              <TabsTrigger value="roles">{t('customers.tabRoles')}</TabsTrigger>
              <TabsTrigger value="actions">
                {t('customers.tabActions')}
              </TabsTrigger>
            </TabsList>

            {/* ------------------------------------------------- profile */}
            <TabsContent value="profile">
              <form
                onSubmit={form.handleSubmit((values) =>
                  updateProfile.mutate(
                    {
                      FirstName: values.firstName,
                      LastName: values.lastName,
                      Email: values.email,
                      PhoneNumber: values.phoneNumber,
                      Gender: values.gender,
                      PreferredLanguage: values.preferredLanguage,
                      ExternalClientId: values.externalClientId || null,
                    },
                    {
                      onSuccess: () => toast.success(t('customers.updated')),
                      onError: () => toast.error(t('customers.updateFailed')),
                    }
                  )
                )}
                className="space-y-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">
                      {t('auth.register.firstName')}
                    </Label>
                    <Input id="firstName" {...form.register('firstName')} />
                    {form.formState.errors.firstName && (
                      <p className="text-destructive text-xs">
                        {t(form.formState.errors.firstName.message!)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">
                      {t('auth.register.lastName')}
                    </Label>
                    <Input id="lastName" {...form.register('lastName')} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <Input id="email" type="email" {...form.register('email')} />
                  {form.formState.errors.email && (
                    <p className="text-destructive text-xs">
                      {t(form.formState.errors.email.message!)}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">
                    {t('auth.register.phone')}
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    {...form.register('phoneNumber')}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('auth.register.gender')}</Label>
                    <Select
                      value={String(form.watch('gender'))}
                      onValueChange={(v) => form.setValue('gender', Number(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(GENDER.male)}>
                          {t('auth.register.male')}
                        </SelectItem>
                        <SelectItem value={String(GENDER.female)}>
                          {t('auth.register.female')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auth.register.preferredLanguage')}</Label>
                    <Select
                      value={String(form.watch('preferredLanguage'))}
                      onValueChange={(v) =>
                        form.setValue('preferredLanguage', Number(v))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={String(LANGUAGE.en)}>
                          {t('common.english')}
                        </SelectItem>
                        <SelectItem value={String(LANGUAGE.es)}>
                          {t('common.spanish')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="externalClientId">
                    {t('customers.externalClientId')}
                  </Label>
                  <Input
                    id="externalClientId"
                    {...form.register('externalClientId')}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={updateProfile.isPending}
                >
                  {updateProfile.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Save />
                  )}
                  {t('common.save')}
                </Button>
              </form>
            </TabsContent>

            {/* --------------------------------------------------- flags */}
            <TabsContent value="flags">
              <p className="text-muted-foreground mb-2 text-xs">
                {t('customers.flagsHint')}
              </p>
              <div className="divide-y rounded-lg border px-3">
                {USER_FLAGS.map((flag) => (
                  <FlagRow
                    key={flag}
                    customer={customer}
                    flag={flag}
                    disabled={!isStoreManager}
                  />
                ))}
              </div>
            </TabsContent>

            {/* --------------------------------------------------- roles */}
            <TabsContent value="roles">
              <p className="text-muted-foreground mb-2 text-xs">
                {t('customers.rolesHint')}
              </p>
              <div className="divide-y rounded-lg border px-3">
                {ASSIGNABLE_ROLES.map((role) => (
                  <RoleRow
                    key={role}
                    customer={customer}
                    role={role}
                    assigned={customer.roles?.includes(role) ?? false}
                    // Owner is admin-only; everything else needs store manager.
                    disabled={role === 'Owner' ? !isAdmin : !isStoreManager}
                  />
                ))}
              </div>
            </TabsContent>

            {/* ------------------------------------------------- actions */}
            <TabsContent value="actions" className="space-y-5">
              <section className="space-y-2">
                <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                  {t('customers.passwordSection')}
                </h3>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <KeyRound />
                      {t('customers.resetPasswordRandom')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('customers.resetPasswordConfirmTitle')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('customers.resetPasswordConfirmBody')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() =>
                          resetRandom.mutate(undefined, {
                            onSuccess: announce,
                            onError: fail,
                          })
                        }
                      >
                        {t('customers.resetPassword')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    placeholder={t('customers.newPassword')}
                  />
                  <Button
                    variant="outline"
                    disabled={!customPassword.trim() || setPassword.isPending}
                    onClick={() =>
                      setPassword.mutate(customPassword, {
                        onSuccess: (message) => {
                          toast.success(message || t('customers.updated'));
                          setCustomPassword('');
                        },
                        onError: fail,
                      })
                    }
                  >
                    {setPassword.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      t('common.save')
                    )}
                  </Button>
                </div>
              </section>

              {isStoreManager && (
                <section className="space-y-2">
                  <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t('customers.codesSection')}
                  </h3>
                  <SecretAction
                    label={t('customers.sendLoginCode')}
                    pending={sendLoginCode.isPending}
                    onRun={() =>
                      sendLoginCode.mutate(undefined, {
                        onSuccess: announce,
                        onError: fail,
                      })
                    }
                  />
                  <SecretAction
                    label={t('customers.resetLoginCode')}
                    pending={resetLoginCode.isPending}
                    onRun={() =>
                      resetLoginCode.mutate(undefined, {
                        onSuccess: announce,
                        onError: fail,
                      })
                    }
                  />
                  <SecretAction
                    label={t('customers.getManagerCode')}
                    pending={managerCode.isPending}
                    onRun={() =>
                      managerCode.mutate(undefined, {
                        onSuccess: announce,
                        onError: fail,
                      })
                    }
                  />
                  {isAdmin && (
                    <SecretAction
                      label={t('customers.generateManagerCode')}
                      pending={generateManagerCode.isPending}
                      onRun={() =>
                        generateManagerCode.mutate(undefined, {
                          onSuccess: announce,
                          onError: fail,
                        })
                      }
                    />
                  )}
                </section>
              )}

              {isAdmin && (
                <section className="space-y-2">
                  <h3 className="text-destructive text-xs font-semibold tracking-wider uppercase">
                    {t('customers.dangerSection')}
                  </h3>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        className="w-full justify-start"
                        disabled={deleteUser.isPending}
                      >
                        {deleteUser.isPending ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <Trash2 />
                        )}
                        {t('customers.deleteUser')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                          <ShieldAlert className="text-destructive size-5" />
                          {t('customers.deleteConfirmTitle')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('customers.deleteConfirmBody', {
                            name:
                              [customer.firstName, customer.lastName]
                                .filter(Boolean)
                                .join(' ') || customer.email,
                          })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t('common.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() =>
                            deleteUser.mutate(undefined, {
                              onSuccess: (message) => {
                                toast.success(
                                  message || t('customers.deleted')
                                );
                                onOpenChange(false);
                              },
                              onError: fail,
                            })
                          }
                        >
                          {t('customers.deleteUser')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </section>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
